import {skipToken} from '@reduxjs/toolkit/query';

import {useContext} from 'react';

import {useGetSourcingCebiaReportQuery} from '@dms/api/vehicle';
import {usePermissions} from '@dms/shared';

import {isNotNilOrEmpty, Nullish, useInterval} from 'shared/web';

import {SourcingCebiaReportContext} from '../contexts/SourcingCebiaReportContext';

const POLLING_INTERVAL_MS = 10000;

export function useSourcingCebiaReport(externalId: string | Nullish) {
  const context = useContext(SourcingCebiaReportContext);
  const [directReadPermission] = usePermissions({permissionKeys: ['readAutotracerPl']});
  const canReadReport = context?.canReadReport ?? directReadPermission;
  const query = useGetSourcingCebiaReportQuery(
    context === null && canReadReport && isNotNilOrEmpty(externalId) ? {externalId} : skipToken
  );
  const reportFromQuery = context === null ? query.data : context.reports[externalId ?? ''];
  const report = canReadReport ? reportFromQuery : undefined;
  const latestCompleted = context === null ? query.data?.latestCompleted : undefined;
  const reportFromContext = context?.reports[externalId ?? ''];
  const latestCompletedReportId = canReadReport
    ? (latestCompleted?.id ?? reportFromContext?.latestCompletedReportId)
    : undefined;
  const latestCompletedVin = canReadReport
    ? (latestCompleted?.vin ?? reportFromContext?.latestCompletedVin)
    : undefined;
  const latestCompletedReportUrlValidTo = canReadReport
    ? (latestCompleted?.reportUrlValidTo ?? reportFromContext?.latestCompletedReportUrlValidTo)
    : undefined;
  const isPending = report?.status === 'accepted' || report?.status === 'pending';

  useInterval(() => query.refetch(), context === null && isPending ? POLLING_INTERVAL_MS : null);

  const refresh = () => {
    if (context !== null) {
      context.refresh();
    } else if (canReadReport && isNotNilOrEmpty(externalId)) {
      query.refetch();
    }
  };

  const isNotFound = query.error && 'status' in query.error && query.error.status === 404;

  return {
    report,
    latestCompletedReportId,
    latestCompletedVin,
    latestCompletedReportUrlValidTo,
    isLoading: context === null ? query.isLoading : context.isLoading,
    isError: context === null ? query.isError && !isNotFound : context.isError,
    canReadReport,
    refresh,
  };
}
